import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Save, Upload, Download, RefreshCw, Activity } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import type { MachineConfigData, DeviceStatus } from '@/types';
import {
  useGetMachineConfig,
  useUpdateMachineConfig,
  usePushMachineConfig,
  usePullMachineConfig,
  useSyncProducts,
  useRequestDeviceStatus,
} from '@/services/api/endpoints/MachineConfigService';

interface MachineConfigPanelProps {
  machineId: string;
}

function ToggleSwitch({
  label,
  checked,
  onChange,
  disabled,
}: {
  label: string;
  checked: boolean;
  onChange: (val: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <div className="flex items-center justify-between py-2">
      <Label className="text-sm">{label}</Label>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
          checked ? 'bg-primary' : 'bg-muted-foreground/30'
        } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
      >
        <span
          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
            checked ? 'translate-x-6' : 'translate-x-1'
          }`}
        />
      </button>
    </div>
  );
}

function formatUptime(ms: number): string {
  const seconds = Math.floor(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  if (days > 0) return `${days}d ${hours % 24}h ${minutes % 60}m`;
  if (hours > 0) return `${hours}h ${minutes % 60}m`;
  return `${minutes}m ${seconds % 60}s`;
}

function StatusRow({ label, value, variant }: { label: string; value: React.ReactNode; variant?: 'default' | 'success' | 'warning' }) {
  return (
    <div className="flex justify-between items-center py-1.5">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="text-sm font-medium">{value}</span>
    </div>
  );
}

function DeviceStatusCard({ status }: { status: DeviceStatus }) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Activity className="w-4 h-4" />
          Device Status
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 gap-x-8">
          <div>
            <StatusRow label="VMC State" value={
              <Badge variant={status.vmc_state === 'IDLE' ? 'default' : 'secondary'}>
                {status.vmc_state || '-'}
              </Badge>
            } />
            <StatusRow label="Reader Enabled" value={status.reader_enabled ? 'Yes' : 'No'} />
            <StatusRow label="Session Active" value={status.session_active ? 'Yes' : 'No'} />
            <StatusRow label="Uptime" value={status.uptime_ms ? formatUptime(status.uptime_ms) : '-'} />
            <StatusRow label="Total Vends" value={status.vend_id ?? '-'} />
          </div>
          <div>
            <StatusRow label="Bill Funds" value={`${status.bill_funds ?? 0} RSD`} />
            <StatusRow label="Coin Funds" value={`${status.coin_funds ?? 0} RSD`} />
            <StatusRow label="Cashless Funds" value={`${status.cashless_funds ?? 0} RSD`} />
            <StatusRow label="Cash Total" value={`${status.cash_funds ?? 0} RSD`} />
            <StatusRow label="User Funds" value={`${status.user_funds ?? 0} RSD`} />
          </div>
        </div>
        {status.timestamp && (
          <div className="mt-3 pt-3 border-t text-xs text-muted-foreground">
            Last status: {new Date(status.timestamp).toLocaleString()}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function NumberField({
  label,
  value,
  onChange,
  disabled,
  suffix,
}: {
  label: string;
  value: number | undefined;
  onChange: (val: number | undefined) => void;
  disabled?: boolean;
  suffix?: string;
}) {
  return (
    <div className="space-y-1">
      <Label className="text-sm">{label}</Label>
      <div className="flex items-center gap-2">
        <Input
          type="number"
          value={value ?? ''}
          onChange={e => {
            const v = e.target.value;
            onChange(v === '' ? undefined : Number(v));
          }}
          disabled={disabled}
          className="h-9"
        />
        {suffix && <span className="text-xs text-muted-foreground whitespace-nowrap">{suffix}</span>}
      </div>
    </div>
  );
}

export function MachineConfigPanel({ machineId }: MachineConfigPanelProps) {
  const { t } = useTranslation();
  const { data: configRecord, isLoading } = useGetMachineConfig(machineId);
  const updateMutation = useUpdateMachineConfig();
  const pushMutation = usePushMachineConfig();
  const pullMutation = usePullMachineConfig();
  const syncMutation = useSyncProducts();
  const statusMutation = useRequestDeviceStatus();

  const [config, setConfig] = useState<MachineConfigData>({});

  useEffect(() => {
    if (configRecord?.config) {
      setConfig(configRecord.config);
    }
  }, [configRecord]);

  const updateField = <K extends keyof MachineConfigData>(key: K, value: MachineConfigData[K]) => {
    setConfig(prev => ({ ...prev, [key]: value }));
  };

  const handleSave = () => {
    updateMutation.mutate(
      { machineId, config },
      {
        onSuccess: () => toast.success(t('machines.configSaved')),
        onError: (err: any) => toast.error(err?.response?.data?.message || t('common.error')),
      }
    );
  };

  const handlePush = () => {
    pushMutation.mutate(machineId, {
      onSuccess: () => toast.success(t('machines.configPushed')),
      onError: (err: any) => toast.error(err?.response?.data?.message || t('common.error')),
    });
  };

  const handlePull = () => {
    pullMutation.mutate(machineId, {
      onSuccess: () => toast.success(t('machines.configPullRequested')),
      onError: (err: any) => toast.error(err?.response?.data?.message || t('common.error')),
    });
  };

  const handleSync = () => {
    syncMutation.mutate(machineId, {
      onSuccess: () => toast.success(t('machines.productsSynced')),
      onError: (err: any) => toast.error(err?.response?.data?.message || t('common.error')),
    });
  };

  const handleRequestStatus = () => {
    statusMutation.mutate(machineId, {
      onSuccess: () => toast.success('Status requested'),
      onError: (err: any) => toast.error(err?.response?.data?.message || t('common.error')),
    });
  };

  const isAnyPending = updateMutation.isPending || pushMutation.isPending || pullMutation.isPending || syncMutation.isPending || statusMutation.isPending;

  if (isLoading) {
    return (
      <div className="space-y-4">
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="h-48" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Action Buttons */}
      <div className="flex flex-wrap gap-2">
        <Button onClick={handleSave} disabled={isAnyPending}>
          <Save className="w-4 h-4 mr-2" />
          {t('common.save')}
        </Button>
        <Button variant="outline" onClick={handlePush} disabled={isAnyPending}>
          <Upload className="w-4 h-4 mr-2" />
          {t('machines.pushToDevice')}
        </Button>
        <Button variant="outline" onClick={handlePull} disabled={isAnyPending}>
          <Download className="w-4 h-4 mr-2" />
          {t('machines.pullFromDevice')}
        </Button>
        <Button variant="outline" onClick={handleSync} disabled={isAnyPending}>
          <RefreshCw className="w-4 h-4 mr-2" />
          {t('machines.syncProducts')}
        </Button>
        <Button variant="outline" onClick={handleRequestStatus} disabled={isAnyPending}>
          <Activity className="w-4 h-4 mr-2" />
          Device Status
        </Button>
      </div>

      {/* Timestamps */}
      {(configRecord?.lastPushedAt || configRecord?.lastReceivedAt) && (
        <div className="flex gap-6 text-sm text-muted-foreground">
          {configRecord.lastPushedAt && (
            <span>{t('machines.lastPushedAt')}: {new Date(configRecord.lastPushedAt).toLocaleString()}</span>
          )}
          {configRecord.lastReceivedAt && (
            <span>{t('machines.lastReceivedAt')}: {new Date(configRecord.lastReceivedAt).toLocaleString()}</span>
          )}
        </div>
      )}

      {/* Device Status */}
      {configRecord?.lastStatus && Object.keys(configRecord.lastStatus).length > 0 && (
        <DeviceStatusCard status={configRecord.lastStatus} />
      )}

      {/* MDB Slave Settings */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">MDB Slave</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4">
            <NumberField
              label="Feature Level"
              value={config.featureLevel}
              onChange={v => updateField('featureLevel', v)}
              disabled={isAnyPending}
            />
            <NumberField
              label="Country Code"
              value={config.countryCode}
              onChange={v => updateField('countryCode', v)}
              disabled={isAnyPending}
            />
            <NumberField
              label="Scale Factor"
              value={config.scaleFactor}
              onChange={v => updateField('scaleFactor', v)}
              disabled={isAnyPending}
            />
            <NumberField
              label="Decimal Places"
              value={config.decimalPlaces}
              onChange={v => updateField('decimalPlaces', v)}
              disabled={isAnyPending}
            />
          </div>
        </CardContent>
      </Card>

      {/* Timeouts */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Timeouts</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4">
            <NumberField
              label="Session Timeout"
              value={config.sessionTimeout}
              onChange={v => updateField('sessionTimeout', v)}
              disabled={isAnyPending}
              suffix="s"
            />
            <NumberField
              label="Vend Timeout"
              value={config.vendTimeout}
              onChange={v => updateField('vendTimeout', v)}
              disabled={isAnyPending}
              suffix="s"
            />
            <NumberField
              label="Manual Session Funds"
              value={config.manualSessionFunds}
              onChange={v => updateField('manualSessionFunds', v)}
              disabled={isAnyPending}
            />
          </div>
          <ToggleSwitch
            label="Always Idle"
            checked={config.alwaysIdle ?? false}
            onChange={v => updateField('alwaysIdle', v)}
            disabled={isAnyPending}
          />
        </CardContent>
      </Card>

      {/* Peripherals */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Peripherals</CardTitle>
        </CardHeader>
        <CardContent>
          <ToggleSwitch
            label="Bill Validator (BV)"
            checked={config.bvEnabled ?? false}
            onChange={v => updateField('bvEnabled', v)}
            disabled={isAnyPending}
          />
          <ToggleSwitch
            label="Coin Changer (CC)"
            checked={config.ccEnabled ?? false}
            onChange={v => updateField('ccEnabled', v)}
            disabled={isAnyPending}
          />
          <ToggleSwitch
            label="Cashless (CSH)"
            checked={config.cshEnabled ?? false}
            onChange={v => updateField('cshEnabled', v)}
            disabled={isAnyPending}
          />
          <ToggleSwitch
            label="NFC"
            checked={config.nfcEnabled ?? false}
            onChange={v => updateField('nfcEnabled', v)}
            disabled={isAnyPending}
          />
        </CardContent>
      </Card>

      {/* Credit Bridge */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Credit Bridge</CardTitle>
        </CardHeader>
        <CardContent>
          <ToggleSwitch
            label="Bridge Enabled"
            checked={config.bridgeEnabled ?? false}
            onChange={v => updateField('bridgeEnabled', v)}
            disabled={isAnyPending}
          />
          <ToggleSwitch
            label="Virtual Credit Enabled"
            checked={config.virtualCreditEnabled ?? false}
            onChange={v => updateField('virtualCreditEnabled', v)}
            disabled={isAnyPending}
          />
          <NumberField
            label="Virtual Credit Amount"
            value={config.virtualCreditAmount}
            onChange={v => updateField('virtualCreditAmount', v)}
            disabled={isAnyPending}
          />
          <div className="mt-2">
            <ToggleSwitch
              label="Auto Return Change"
              checked={config.autoReturnChange ?? false}
              onChange={v => updateField('autoReturnChange', v)}
              disabled={isAnyPending}
            />
            <ToggleSwitch
              label="Auto Start Session"
              checked={config.autoStartSession ?? false}
              onChange={v => updateField('autoStartSession', v)}
              disabled={isAnyPending}
            />
          </div>
        </CardContent>
      </Card>

      {/* Payment Timing */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Payment Timing</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4">
            <NumberField
              label="Vend Info Cash Min"
              value={config.vendInfoCashMinMs}
              onChange={v => updateField('vendInfoCashMinMs', v)}
              disabled={isAnyPending}
              suffix="ms"
            />
            <NumberField
              label="Vend Info Card Prelaunch"
              value={config.vendInfoCardPrelaunchMs}
              onChange={v => updateField('vendInfoCardPrelaunchMs', v)}
              disabled={isAnyPending}
              suffix="ms"
            />
            <NumberField
              label="Vend Retry Lock Timeout"
              value={config.vendRetryLockTimeoutMs}
              onChange={v => updateField('vendRetryLockTimeoutMs', v)}
              disabled={isAnyPending}
              suffix="ms"
            />
          </div>
          <div className="mt-2">
            <ToggleSwitch
              label="Late Payment Use Void"
              checked={config.latePaymentUseVoid ?? false}
              onChange={v => updateField('latePaymentUseVoid', v)}
              disabled={isAnyPending}
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
